// Portfolio hub + hash router (#/portfolio, #/portfolio/<section key>, #/portfolio/<section key>/<slug>). Sections are editable (admin): see SectionDialog.js.
// New component (does not modify PortfolioView). Sections: VideoWorkspace, PhotoWorkspace, UxWorkspace (+ CaseStudyPage).
(function () {
  const { Box, Typography, Grid } = MaterialUI;
  const { useState, useEffect } = React;

  const MIN_SECTIONS = 2; // the selector shows only above this many sections

  // About me lives at #/portfolio[/<section>[/<slug>]]; every other page at #/p/<page key>[/<section>[/<slug>]].
  function parseHash(page) {
    const parts = (window.location.hash || '').replace(/^#\/?/, '').split('/').filter(Boolean);
    const rest = page === 'about' ? (parts[0] === 'portfolio' ? parts.slice(1) : null) : (parts[0] === 'p' && parts[1] === page ? parts.slice(2) : null);
    return rest ? { section: rest[0] || null, slug: rest[1] || null } : { section: null, slug: null };
  }

  function navigate(page, section, slug) {
    const path = [...(page === 'about' ? ['portfolio'] : ['p', page]), section, slug].filter(Boolean).join('/');
    window.location.hash = '#/' + path;
  }

  // Studies of the section a visit lands on, requested together with the section list instead of after it (one round trip
  // less before content shows). Used once by SectionContent if the keys match; later loads always fetch.
  let prefetched = null; // { key, promise, at }
  function prefetchStudies(page, route) {
    const key = route.section || (page === 'about' ? 'ux' : null);
    prefetched = key ? { key, promise: window.PortfolioApi.list(key), at: Date.now() } : null;
    if (prefetched) prefetched.promise.catch(() => {}); // a failure is handled when it is used
  }
  function takePrefetched(key) {
    const p = prefetched && prefetched.key === key && Date.now() - prefetched.at < 15000 ? prefetched.promise : null;
    prefetched = null;
    return p;
  }

  // Which workspace each template uses. Blog and coding blog reuse the case-study workspace (blog adds search, sort and dates).
  // page: key of the portfolio page shown (see portfolio-pages.js). Every page has the same layout: hero, section tabs, section content.
  function PortfolioHub({ page = 'about', pageTitle = '' }) {
    const { PortfolioHero, Toolbar, VideoWorkspace, UxWorkspace, PhotoWorkspace, SectionDialog, SectionEmpty, Button, IconButton, Tooltip, GoogleIcon, PortfolioEmbedContext, DoodleBackdrop, DoodlePanel, DoodleEditButton } = window.Components;
    const api = window.PortfolioApi;
    const [route, setRoute] = useState(() => parseHash(page));
    // Light deterrent: no right-click menu or dragging on images (served copies are also downscaled, see server.js).
    useEffect(() => {
      const block = e => { if (e.target && e.target.tagName === 'IMG') e.preventDefault(); };
      document.addEventListener('contextmenu', block);
      document.addEventListener('dragstart', block);
      return () => { document.removeEventListener('contextmenu', block); document.removeEventListener('dragstart', block); };
    }, []);
    const [sections, setSections] = useState(null);
    const [templates, setTemplates] = useState({});
    const [canEdit, setCanEdit] = useState(false);
    const editOn = window.EditMode.use();
    const [dialog, setDialog] = useState(null); // null | { section }
    const [error, setError] = useState('');

    const loadSections = React.useCallback(async () => {
      try {
        const res = await api.sections.list(page);
        setSections(res.sections.filter(s => editOn || s.published !== false)); setTemplates(res.templates); setCanEdit(!!res.canEdit && editOn);
      } catch (e) { setError(e.message); setSections(prev => prev || []); }
    }, [editOn, page]);
    useEffect(() => { prefetchStudies(page, parseHash(page)); }, [page]);
    useEffect(() => { loadSections(); }, [loadSections]);

    useEffect(() => {
      const onChange = () => setRoute(parseHash(page));
      window.addEventListener('hashchange', onChange);
      return () => window.removeEventListener('hashchange', onChange);
    }, [page]);

    // Landing on #/portfolio opens UX (or the first section if there is no UX section).
    const current = sections && (sections.find(s => s.key === route.section) || (!route.section ? sections.find(s => s.key === 'ux') || sections[0] : undefined));
    // Doodle target: the open study page, else the section, else the hub itself. A study without its own doodle uses its section's.
    const sectionTarget = current ? `section:${current.key}` : (page === 'about' ? 'section:portfolio' : `page:${page}`);
    const item = route.slug && route.slug !== 'all' && !route.slug.startsWith('video-') ? route.slug : null; // 'all' (Photography's All photos) and video-<id> are views, not pages
    const doodleTarget = item ? `study:${item}` : sectionTarget;
    const own = window.Doodle.useDoodle(doodleTarget).doodle;
    const inherited = window.Doodle.useDoodle(sectionTarget).doodle;
    const doodle = own || inherited;
    const move = async dir => {
      const keys = sections.map(s => s.key);
      const i = keys.indexOf(current.key);
      const j = i + dir;
      if (j < 0 || j >= keys.length) return;
      [keys[i], keys[j]] = [keys[j], keys[i]];
      try { await api.sections.order(keys); await loadSections(); } catch (e) { setError(e.message); }
    };

    const onDialogSaved = async (saved, what) => {
      const before = dialog && dialog.section;
      setDialog(null);
      await loadSections();
      if (what === 'created') { navigate(page, saved.key); window.notify(`Section “${saved.title}” created`); }
      else if (what === 'moved') { navigate(saved.page, saved.key); window.notify(`“${saved.title}” moved to another page`); } // follow it to its new page
      else if (what === 'deleted') {
        navigate(page);
        window.notify(`“${saved.title}” moved to trash`, {
          actionLabel: 'Undo',
          onAction: async () => {
            try { const r = await api.trash.restore('section', saved.key); await loadSections(); navigate(r.page, saved.key); window.notify(`“${saved.title}” restored`); } catch (e) { window.notify(e.message); }
          }
        });
      } else if (before && before.published !== saved.published) window.notify(saved.published ? `“${saved.title}” is now visible to everyone` : `“${saved.title}” is now hidden`);
      else window.notify('Section saved');
    };

    // A section that no longer exists (deleted or hidden in another tab, or an old link): back to the page's own start, replacing the dead address
    const gone = !!sections && !!route.section && !sections.some(s => s.key === route.section);
    useEffect(() => {
      if (!gone) return;
      window.location.replace('#/' + (page === 'about' ? 'portfolio' : `p/${page}`));
    }, [gone, page]);

    if (sections === null) return null;

    return (
      <Box component="section" aria-labelledby="portfolio-title" sx={{ position: 'relative', isolation: 'isolate' }}>
        {doodle && <DoodleBackdrop config={doodle} />}
        {/* The blurred layer under the page items covers the whole page, hero and section tabs included */}
        <DoodlePanel active={!!doodle}>
        <PortfolioHero key={page} page={page} section={current} onSectionChanged={loadSections} defaultPending={!sections && !route.section} />
        {error && <window.Components.Banner severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</window.Components.Banner>}

        {sections.length === 0 ? (
          <SectionEmpty canEdit={canEdit} onCreate={() => setDialog({ section: null })} />
        ) : (
          <>
            {/* The section selector only appears with more than MIN_SECTIONS sections; with fewer, the page just shows its section */}
            {sections.length > MIN_SECTIONS && (
              <Toolbar
                label="Portfolio sections"
                tone="standard"
                fullWidth
                sx={{ backgroundColor: 'var(--m3-surface-container-low)', boxShadow: 'none' }}
                items={sections.map(s => ({ key: s.key, label: s.published ? s.title : `${s.title} (hidden)`, selected: current === s, onClick: () => navigate(page, s.key) }))}
              />
            )}
            {canEdit && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 2 }}>
                <Button variant="tonal" size="small" icon={<GoogleIcon name="add" size={18} />} onClick={() => setDialog({ section: null })}>New section</Button>
                <DoodleEditButton target={doodleTarget} title={item ? 'Doodle for this page' : `Doodle for ${current ? current.title : 'the portfolio'}`} label={item ? 'Page doodle' : 'Doodle'} />
                {current && (
                  <>
                    <Button variant="text" size="small" icon={<GoogleIcon name="edit" size={18} />} onClick={() => setDialog({ section: current })}>Edit section</Button>
                    <Tooltip title="Move left"><span><IconButton size="small" aria-label="Move section left" disabled={sections[0] === current} onClick={() => move(-1)}><GoogleIcon name="arrow_back" size={18} /></IconButton></span></Tooltip>
                    <Tooltip title="Move right"><span><IconButton size="small" aria-label="Move section right" disabled={sections[sections.length - 1] === current} onClick={() => move(1)}><GoogleIcon name="arrow_forward" size={18} /></IconButton></span></Tooltip>
                  </>
                )}
              </Box>
            )}
          </>
        )}

        {current && (
          <Box sx={{ mt: 4 }}>
            <PortfolioEmbedContext.Provider value={true}>
              <SectionContent key={current.key} page={page} section={current} slug={route.slug} />
            </PortfolioEmbedContext.Provider>
          </Box>
        )}
        </DoodlePanel>

        {dialog && <SectionDialog section={dialog.section} page={page} templates={templates} onClose={() => setDialog(null)} onSaved={onDialogSaved} />}
      </Box>
    );
  }

  // Renders one section with the layout of its template. Case-study style sections load their studies here.
  function SectionContent({ section, slug, page }) {
    const { VideoWorkspace, UxWorkspace, PhotoWorkspace } = window.Components;
    const [studies, setStudies] = useState(null);
    const [canEdit, setCanEdit] = useState(false);
    const editOn = window.EditMode.use();
    const t = section.template;
    const studyBased = t === 'ux' || t === 'blog' || t === 'coding' || t === 'blank' || t === 'home';

    const reload = React.useCallback(async (selectSlug) => {
      try {
        const res = await (takePrefetched(section.key) || window.PortfolioApi.list(section.key));
        setStudies(res.studies.filter(s => editOn || s.published !== false)); setCanEdit(!!res.canEdit && editOn);
      } catch (e) {
        setStudies(prev => prev || []);
      }
      if (selectSlug) navigate(page, section.key, selectSlug);
    }, [section.key, editOn]);
    useEffect(() => { if (studyBased) reload(); }, [reload]);

    if (t === 'video') return <VideoWorkspace section={section.key} slug={slug} />;
    if (t === 'photography') return <PhotoWorkspace section={section.key} slug={slug} onNavigate={s => navigate(page, section.key, s)} />;
    if (studies === null) return null;
    return (
      <UxWorkspace
        studies={studies} slug={slug} section={section.key} template={t} title={section.title}
        onSelect={s => navigate(page, section.key, s)} canEdit={canEdit} reload={reload}
      />
    );
  }

  window.Components = window.Components || {};
  window.Components.PortfolioHub = PortfolioHub;
})();
